<script setup lang="ts">
import { ref } from 'vue'
import { get, post, put } from '@/api'
import { driverConfigActionsApi } from '@/api/entities/review/driverConfigActions'

definePage({
  meta: {
    title: '다운로드 드라이버 관리',
    action: 'read',
    subject: 'Admin',
  },
})

const { snackbar, handleError } = useErrorHandler()

// 다운로드 드라이버 인터페이스
interface DownloadDriver {
  id: number | null
  name: string | null
  code: string | null
  spec: string | null
  description: string | null
}

// 초기 드라이버 모델
const getInitialDriver = (): DownloadDriver => ({
  id: null,
  name: null,
  code: null,
  spec: null,
  description: null,
})

// 상태
const selectedDriver = ref<DownloadDriver>(getInitialDriver())
const drivers = ref<DownloadDriver[]>([])
const loading = ref(false)
const saving = ref(false)

// 테이블 헤더
const headers = [
  { title: '드라이버 이름', key: 'name' },
  { title: '드라이버 코드', key: 'code' },
  { title: '드라이버 스펙', key: 'spec', width: '200px' },
  { title: '드라이버 설명', key: 'description' },
  { title: '작업', key: 'actions', sortable: false },
]

// 운영 액션
const actionLoading = ref<string | null>(null)

type DriverConfigAction = 'syncZzFulfillmentType' | 'collectImageInfo'

const runDriverAction = async (
  actionName: DriverConfigAction,
  driver: DownloadDriver,
) => {
  if (driver.id === null)
    return
  actionLoading.value = `${actionName}-${driver.id}`
  try {
    const result = await driverConfigActionsApi[actionName](driver.id)
    if (result.success) {
      snackbar.success('백그라운드에서 처리 중입니다.')
    }
    else {
      snackbar.error('액션 실행에 실패했습니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '액션 실행 중 오류가 발생했습니다.' })
  }
  finally {
    actionLoading.value = null
  }
}

// 스펙 미리보기 포맷
const formatSpec = (spec: string | null) => {
  if (!spec)
    return '-'
  try {
    const parsed = typeof spec === 'string' ? JSON.parse(spec) : spec

    return JSON.stringify(parsed).substring(0, 50) + (JSON.stringify(parsed).length > 50 ? '...' : '')
  }
  catch {
    return String(spec).substring(0, 50) + (String(spec).length > 50 ? '...' : '')
  }
}

// 드라이버 목록 로드
const loadDrivers = async () => {
  loading.value = true
  try {
    const { success, data, error } = await get<DownloadDriver[] | { data: DownloadDriver[] }>('/review-download-driver')
    if (success && data) {
      // 배열 또는 {data: [...]} 형식 모두 처리
      drivers.value = Array.isArray(data) ? data : (data.data || [])
    }
    else if (error) {
      handleError(error, { notificationMessage: '드라이버 목록을 불러오는데 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '드라이버 목록을 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 드라이버 선택
const selectDriver = (driver: DownloadDriver) => {
  const cloned = JSON.parse(JSON.stringify(driver))

  // spec을 문자열로 변환
  if (cloned.spec && typeof cloned.spec === 'object')
    cloned.spec = JSON.stringify(cloned.spec, null, 2)

  selectedDriver.value = cloned
}

// 저장 전 필수 필드 및 spec JSON 유효성 검사. 통과 시 true.
const validateDriverForSave = (): boolean => {
  if (!selectedDriver.value.name) {
    snackbar.warning('드라이버 이름을 입력해주세요.')

    return false
  }

  if (!selectedDriver.value.id && !selectedDriver.value.code) {
    snackbar.warning('드라이버 코드를 입력해주세요.')

    return false
  }

  if (selectedDriver.value.spec) {
    try {
      JSON.parse(selectedDriver.value.spec)
    }
    catch {
      snackbar.warning('드라이버 스펙이 유효한 JSON 형식이 아닙니다.')

      return false
    }
  }

  return true
}

// 전송용 페이로드 구성 (spec 을 객체로 파싱). 위에서 이미 JSON 검증을 거쳤으므로 안전.
const buildDriverPayload = () => {
  const payload = { ...selectedDriver.value } as Record<string, unknown> & { spec?: unknown }

  if (typeof payload.spec === 'string' && payload.spec)
    payload.spec = JSON.parse(payload.spec)

  return payload
}

// 드라이버 저장
const saveDriver = async () => {
  if (!validateDriverForSave())
    return

  saving.value = true
  try {
    const payload = buildDriverPayload()
    const isUpdate = selectedDriver.value.id !== null

    const url = isUpdate
      ? `/review-download-driver/${selectedDriver.value.id}`
      : '/review-download-driver'

    const { success, error } = isUpdate
      ? await put(url, payload)
      : await post(url, payload)

    if (success) {
      snackbar.success(isUpdate ? '드라이버가 수정되었습니다.' : '드라이버가 등록되었습니다.')
      await loadDrivers()
      if (!isUpdate)
        resetForm()
    }
    else if (error) {
      handleError(error, { notificationMessage: '저장에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '저장에 실패했습니다.' })
  }
  finally {
    saving.value = false
  }
}

// 폼 초기화
const resetForm = () => {
  selectedDriver.value = getInitialDriver()
}

// 초기 로드
onMounted(() => {
  loadDrivers()
})
</script>

<template>
  <VRow>
    <!-- 드라이버 등록/수정 폼 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>다운로드 드라이버 등록 및 수정</VCardTitle>
        <VCardText>
          <div class="text-h6 mb-4">
            드라이버 정보
          </div>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedDriver.name"
                label="드라이버 이름"
                placeholder="드라이버 이름을 입력하세요"
                required
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedDriver.code"
                label="드라이버 코드"
                placeholder="드라이버 코드를 입력하세요"
                :disabled="selectedDriver.id !== null"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol cols="12">
              <VTextarea
                v-model="selectedDriver.spec"
                label="드라이버 스펙 (JSON)"
                placeholder="{&quot;profileUrl&quot;: &quot;...&quot;, &quot;type&quot;: &quot;...&quot;}"
                rows="4"
                hint="JSON 형식으로 입력하세요"
                persistent-hint
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="selectedDriver.description"
                label="드라이버 설명"
                placeholder="드라이버 설명을 입력하세요"
              />
            </VCol>
          </VRow>

          <VRow class="mt-4">
            <VCol>
              <VBtn
                v-if="selectedDriver.id"
                variant="outlined"
                class="me-2"
                @click="resetForm"
              >
                초기화
              </VBtn>
              <VBtn
                color="primary"
                :loading="saving"
                @click="saveDriver"
              >
                {{ selectedDriver.id ? '수정하기' : '저장하기' }}
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 드라이버 목록 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>다운로드 드라이버 목록</span>
          <VBtn
            icon="tabler-refresh"
            variant="text"
            :loading="loading"
            @click="loadDrivers"
          />
        </VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headers"
            :items="drivers"
            :loading="loading"
            hover
            density="compact"
            class="cursor-pointer"
            @click:row="(_event: Event, { item }: { item: DownloadDriver }) => selectDriver(item)"
          >
            <template #item.name="{ item }">
              {{ item.name || '-' }}
            </template>

            <template #item.code="{ item }">
              <code>{{ item.code || '-' }}</code>
            </template>

            <template #item.spec="{ item }">
              <span
                class="text-truncate d-inline-block"
                style="max-width: 200px;"
              >
                {{ formatSpec(item.spec) }}
              </span>
            </template>

            <template #item.description="{ item }">
              {{ item.description || '-' }}
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex gap-2 flex-wrap">
                <VBtn
                  v-if="item.code === 'zigzag'"
                  size="small"
                  color="info"
                  variant="tonal"
                  :loading="actionLoading === `syncZzFulfillmentType-${item.id}`"
                  @click="runDriverAction('syncZzFulfillmentType', item)"
                >
                  배송 유형 동기화
                </VBtn>
                <VBtn
                  size="small"
                  color="secondary"
                  variant="tonal"
                  :loading="actionLoading === `collectImageInfo-${item.id}`"
                  @click="runDriverAction('collectImageInfo', item)"
                >
                  이미지 정보 수집
                </VBtn>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.cursor-pointer :deep(tbody tr) {
  cursor: pointer;
}
</style>
